import VehiclePlateIcon from '@/common/assets/icons/vehiclePlateIcon.svg?react';
import { freeZonePlateRules } from '@/common/components/base/input/plate/freeZonePlateInput/freeZonePlateInput.rules';
import {
  FreeZonePlatePropsType,
  FreeZonePlateValue,
} from '@/common/components/base/input/plate/freeZonePlateInput/freeZonePlateInput.type';
import usePlateFocusInputs from '@/common/components/base/input/plate/hook/usePlateFocusInputs';

import NumberInput from '@/common/components/base/input/textInput copy/NumberInput';
import Typography from '@/common/components/base/typography/Typography';
import { FC, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';

const defaultValue: FreeZonePlateValue['plate'] = {
  vehicle_plate_top: '',
  vehicle_plate_bottom: '',
};
const FreeZonePlateInput: FC<FreeZonePlatePropsType> = ({
  onChange,
  value: plateValue,
  vehicle_plate_design = 'free_zone',
  vehicle_type_id = 1,
  requiredError,
}) => {
  const methods = useForm<any>({
    defaultValues: {
      vehicle_plate_top: plateValue?.plate?.vehicle_plate_top || '',
      vehicle_plate_bottom: plateValue?.plate?.vehicle_plate_bottom || '',
    },
    mode: 'onChange',
  });
  const formData = useWatch({ control: methods.control });
  const { focus } = usePlateFocusInputs<keyof typeof defaultValue>({ methods });
  const isMotorFreeZone =
    vehicle_type_id === 2 && vehicle_plate_design === 'free_zone';
  useEffect(() => {
    const { vehicle_plate_top, vehicle_plate_bottom } = formData;
    const plate = isMotorFreeZone
      ? vehicle_plate_bottom
      : `${vehicle_plate_top}${vehicle_plate_bottom}`;
    onChange({
      plateString: plate,
      plate: {
        ...formData,
      },
    });
  }, [formData]);

  const plate = () => {
    if (vehicle_type_id === 2 && vehicle_plate_design === 'free_zone') {
      return 'motorFreeZone';
    }
    if (vehicle_type_id === 2 && vehicle_plate_design !== 'free_zone') {
      return 'motor';
    }

    if (vehicle_type_id !== 2 && vehicle_plate_design === 'free_zone') {
      return 'vehicleFreeZone';
    }
  };

  return (
    <FormProvider {...methods}>
      <Box>
        <Typography>پلاک</Typography>
        <Box
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            width: '65%',
            border: '1px solid',
            borderColor: requiredError ? 'red' : 'gray',
            borderRadius: '8px',
            backgroundColor: 'gray-3',
          })}
        >
          <Box
            display="flex"
            alignItems="center"
            gap="1rem"
            mr="8px"
            width={
              vehicle_type_id === 2 && vehicle_plate_design === 'free_zone'
                ? '100%'
                : 'initial'
            }
          >
            {plate() === 'motor' ? (
              <>
                <Box flex="3">
                  <NumberInput
                    onChange={(value) =>
                      focus({
                        value,
                        nextName: '',
                        previous: 'vehicle_plate_top',
                        length: 2,
                      })
                    }
                    name="vehicle_plate_top"
                    className={inputStyle}
                    maxLength={3}
                    rules={freeZonePlateRules.vehicle_plate_top}
                    inputProps={{ textAlign: 'center' }}
                    placeholder="555"
                  />
                </Box>
                <Box
                  borderRight="1px solid"
                  borderColor="black-4"
                  w="1px"
                  height="2.2rem"
                />
                <Box flex="6">
                  <NumberInput
                    onChange={(value) =>
                      focus({
                        value,
                        nextName: 'vehicle_plate_bottom',
                        previous: '',
                        length: 5,
                      })
                    }
                    maxLength={5}
                    name="vehicle_plate_bottom"
                    className={inputStyle}
                    rules={freeZonePlateRules.vehicle_plate_bottom}
                    inputProps={{ textAlign: 'center' }}
                    placeholder="55555"
                  />
                </Box>
              </>
            ) : plate() === 'motorFreeZone' ? (
              <>
                <Box width="100%">
                  <NumberInput
                    onChange={(value) =>
                      focus({
                        value,
                        nextName: 'vehicle_plate_bottom',
                        previous: '',
                        length: 5,
                      })
                    }
                    maxLength={5}
                    name="vehicle_plate_bottom"
                    className={inputStyle}
                    rules={freeZonePlateRules.vehicle_plate_bottom}
                    inputProps={{ textAlign: 'center' }}
                    placeholder="55555"
                  />
                </Box>
              </>
            ) : (
              <>
                <Box flex="6">
                  <NumberInput
                    onChange={(value) =>
                      focus({
                        value,
                        nextName: 'vehicle_plate_bottom',
                        previous: '',
                        length: 5,
                      })
                    }
                    maxLength={5}
                    name="vehicle_plate_bottom"
                    className={inputStyle}
                    rules={freeZonePlateRules.vehicle_plate_bottom}
                    inputProps={{ textAlign: 'center' }}
                    placeholder="55555"
                  />
                </Box>
                <Box
                  borderRight="1px solid"
                  borderColor="black-4"
                  w="1px"
                  height="2.2rem"
                />
                {
                  <Box flex="3">
                    <NumberInput
                      onChange={(value) =>
                        focus({
                          value,
                          nextName: '',
                          previous: 'vehicle_plate_top',
                          length: 2,
                        })
                      }
                      name="vehicle_plate_top"
                      className={inputStyle}
                      maxLength={2}
                      optionalLabel
                      inputProps={{ textAlign: 'center' }}
                      placeholder="55"
                    />
                  </Box>
                }
              </>
            )}
          </Box>
          <Box>
            <VehiclePlateIcon
              className={css({ borderRadius: '8px 0 0 8px' })}
            />
          </Box>
        </Box>
      </Box>
      {requiredError && (
        <Typography height="24px" color="red-1" textStyle="b4">
          {requiredError}
        </Typography>
      )}
    </FormProvider>
  );
};

export default FreeZonePlateInput;

const inputStyle = css({
  border: '0px',
  minHeight: '32px',
  p: 0,
});
